import type { UserBrief } from '@ovo/shared'
import { useMemo, useState } from 'react'
import { useUsers } from '../api/hooks.ts'
import { useDebounced } from '../lib/hooks.ts'
import { haptic } from '../telegram/haptics.ts'
import { Avatar } from './Avatar.tsx'
import { EmptyState } from './EmptyState.tsx'
import { Input } from './Input.tsx'
import { IconCheck, IconSearch } from './icons.tsx'
import { Sheet } from './Sheet.tsx'
import { SkeletonList } from './Skeleton.tsx'

interface PersonPickerProps {
  open: boolean
  onClose: () => void
  title?: string
  meId?: number
  /** Кандидаты от ИИ — показываем первыми. */
  candidates?: UserBrief[]
  excludeIds?: number[]
  /** Одиночный выбор. */
  onPick?: (user: UserBrief) => void
  /** Множественный выбор (наблюдатели). */
  multiple?: boolean
  selectedUsers?: UserBrief[]
  onDone?: (users: UserBrief[]) => void
}

const NO_DEPT = 'Без отдела'

function PersonRow({
  user,
  label,
  selected,
  onClick,
}: {
  user: UserBrief
  label?: string
  selected?: boolean
  onClick: () => void
}) {
  return (
    <button type="button" className="cell" onClick={onClick} aria-pressed={selected}>
      <span className="cell__before">
        <Avatar name={user.displayName} id={user.id} size={36} />
      </span>
      <span className="cell__main">
        <span className="cell__title">{label ?? user.displayName}</span>
        <span className="cell__subtitle">
          {[user.position, user.departmentName].filter(Boolean).join(' · ') || 'Сотрудник'}
        </span>
      </span>
      {selected && (
        <span className="cell__after cell__after--done">
          <IconCheck size={22} strokeWidth={2.4} />
        </span>
      )}
    </button>
  )
}

function PickerBody({
  meId,
  candidates,
  excludeIds,
  onPick,
  multiple,
  selected,
  toggle,
}: {
  meId?: number
  candidates?: UserBrief[]
  excludeIds?: number[]
  onPick?: (u: UserBrief) => void
  multiple?: boolean
  selected: Map<number, UserBrief>
  toggle: (u: UserBrief) => void
}) {
  const [q, setQ] = useState('')
  const debounced = useDebounced(q.trim(), 200)
  const users = useUsers(debounced)

  const groups = useMemo(() => {
    const exclude = new Set(excludeIds ?? [])
    const list = (users.data ?? []).filter((u) => !exclude.has(u.id) && u.id !== meId)
    const map = new Map<string, UserBrief[]>()
    for (const u of list) {
      const key = u.departmentName ?? NO_DEPT
      map.set(key, [...(map.get(key) ?? []), u])
    }
    return [...map.entries()].sort(([a], [b]) =>
      a === NO_DEPT ? 1 : b === NO_DEPT ? -1 : a.localeCompare(b, 'ru'),
    )
  }, [users.data, excludeIds, meId])

  const me = meId ? users.data?.find((u) => u.id === meId) : undefined
  const pick = (u: UserBrief): void => {
    haptic.selection()
    if (multiple) toggle(u)
    else onPick?.(u)
  }

  return (
    <>
      <div className="search-field">
        <span className="search-field__icon">
          <IconSearch size={22} />
        </span>
        <Input
          type="search"
          placeholder="Имя, должность или отдел"
          aria-label="Поиск сотрудника"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-autofocus
          enterKeyHint="search"
        />
      </div>

      {!q && candidates && candidates.length > 0 && (
        <section className="section">
          <div className="section__header">Возможно, это</div>
          <div className="section__body">
            {candidates.map((u) => (
              <PersonRow key={u.id} user={u} selected={selected.has(u.id)} onClick={() => pick(u)} />
            ))}
          </div>
        </section>
      )}

      {!q && me && !multiple && (
        <div className="section__body">
          <PersonRow user={me} label="Мне" onClick={() => pick(me)} />
        </div>
      )}

      {users.isPending ? (
        <SkeletonList rows={5} />
      ) : groups.length === 0 ? (
        <EmptyState title="Никого не нашли" text="Проверьте написание или поищите по отделу" />
      ) : (
        groups.map(([dept, list]) => (
          <section className="section" key={dept}>
            <div className="section__header">
              {dept}
              <span className="section__count">{list.length}</span>
            </div>
            <div className="section__body">
              {list.map((u) => (
                <PersonRow key={u.id} user={u} selected={selected.has(u.id)} onClick={() => pick(u)} />
              ))}
            </div>
          </section>
        ))
      )}
    </>
  )
}

/** Выбор сотрудника: поиск по имени/алиасам, группы по отделам. */
export function PersonPicker({
  open,
  onClose,
  title = 'Кому поставить',
  meId,
  candidates,
  excludeIds,
  onPick,
  multiple,
  selectedUsers,
  onDone,
}: PersonPickerProps) {
  const [selected, setSelected] = useState<Map<number, UserBrief>>(new Map())
  const [prevOpen, setPrevOpen] = useState(false)
  // при открытии подхватываем текущий выбор
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setSelected(new Map((selectedUsers ?? []).map((u) => [u.id, u])))
  }

  const toggle = (u: UserBrief): void => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(u.id)) next.delete(u.id)
      else next.set(u.id, u)
      return next
    })
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      full
      primary={
        multiple
          ? {
              text: selected.size ? `Готово · ${selected.size}` : 'Готово',
              onClick: () => onDone?.([...selected.values()]),
            }
          : undefined
      }
    >
      <PickerBody
        meId={meId}
        candidates={candidates}
        excludeIds={excludeIds}
        onPick={onPick}
        multiple={multiple}
        selected={selected}
        toggle={toggle}
      />
    </Sheet>
  )
}
